import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Paper,
  TablePagination,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Box,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

import StudentListModal from "./StudentListModal";
import AdminListModal from "./AdminListModal";

// Styled TableCell
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#7e7e7e",
    color: theme.palette.common.white,
    textTransform: "capitalize",
    fontSize: 16,
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    textTransform: "capitalize",
  },
}));

// Styled TableRow
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd),&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
    borderBottom: "1px solid rgba(224, 224, 224, 1)",
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const CoeTable = ({ rows, loading, error, onEditClick, disableCoe, fetchCoeData, user }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [studentListType, setStudentListType] = useState('active');
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminListType, setAdminListType] = useState('admin');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedCoe, setSelectedCoe] = useState(null);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenAdminModal = (row) => {
    setSelectedRow(row || []);
    setIsAdmin(true);
    setAdminListType('admin');
  };

  const handleOpenViewerModal = (row) => {
    setSelectedRow(row || []);
    setIsAdmin(true);
    setAdminListType('viewer');
  };

  const handleCloseAdminModal = () => {
    setIsAdmin(false);
    setSelectedRow([]);
  };

  const handleOpenModal = (students, type) => {
    setSelectedRow(students || []);
    setStudentListType(type || 'active');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRow([]);
  };

  const handleDisableClick = (row) => {
    setSelectedCoe(row);
    setConfirmOpen(true);
  };

  const handleConfirmDisable = () => {
    if (selectedCoe) {
      disableCoe(selectedCoe.id);
      setSelectedCoe(null);
      setConfirmOpen(false);
    }
  };

  const handleCancelDisable = () => {
    setSelectedCoe(null);
    setConfirmOpen(false);
  };

  const paginatedRows = rows?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return <Typography variant="h6" align="center">Loading...</Typography>;
  }

  if (error) {
    return (
      <Typography variant="h6" align="center" color="error">
        {error}
      </Typography>
    );
  }

  return (
    <>
      <TableContainer component={Paper} sx={{ width: "100%", overflowX: "auto" }}>
        <Table sx={{ tableLayout: "auto", width: "max-content", minWidth: "100%" }} aria-label="customized table">
          <TableHead>
            <TableRow>
              <StyledTableCell>COE Name</StyledTableCell>
              <StyledTableCell align="center">Admins#</StyledTableCell>
              <StyledTableCell>COE Details</StyledTableCell>
              <StyledTableCell>Latitude</StyledTableCell>
              <StyledTableCell>Longitude</StyledTableCell>
              <StyledTableCell align="center">Active Learners#</StyledTableCell>
              <StyledTableCell align="center">Inactive Learners#</StyledTableCell>
              <StyledTableCell align="center">Viewers#</StyledTableCell>
              {user?.role === 'super_admin' &&
                <StyledTableCell>Actions</StyledTableCell>
              }
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <StyledTableRow>
                <StyledTableCell colSpan={user?.role === 'super_admin' ? 9 : 8} align="center" sx={{ py: 6 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
                    <CircularProgress size={36} />
                    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
                      Loading COE data...
                    </Typography>
                  </Box>
                </StyledTableCell>
              </StyledTableRow>
            ) : paginatedRows?.length > 0 ? (
              paginatedRows.map((row, index) => (
                <StyledTableRow key={index}>
                  <StyledTableCell>{row.name || "-"}</StyledTableCell>
                  <StyledTableCell
                    align="center"
                    onClick={() => handleOpenAdminModal(row)}
                    style={{ cursor: "pointer", color: "blue" }}
                  >
                    {(row.coe_admin_count || 0) + (row.report_admin_count || 0)}
                  </StyledTableCell>
                  <StyledTableCell>{row.coe_detail || "-"}</StyledTableCell>
                  <StyledTableCell>{row.latitude || "-"}</StyledTableCell>
                  <StyledTableCell>{row.longitude || "-"}</StyledTableCell>
                  <StyledTableCell
                    align="center"
                    onClick={() => handleOpenModal(row, 'active')}
                    style={{ cursor: "pointer", color: "blue" }}
                  >
                    {row.activelearner_count || 0}
                  </StyledTableCell>
                  <StyledTableCell
                    align="center"
                    onClick={() => handleOpenModal(row, 'inactive')}
                    style={{ cursor: "pointer", color: "blue" }}
                  >
                    {row.inactice_learners_count || 0}
                  </StyledTableCell>
                   <StyledTableCell
                    align="center"
                    onClick={() => handleOpenViewerModal(row)}
                    style={{ cursor: "pointer", color: "blue" }}
                  >
                    {row.coe_viewers_count || 0}
                  </StyledTableCell>
                  {user?.role === 'super_admin' && (
                    <StyledTableCell>
                      <Tooltip title="Edit" arrow>
                        <IconButton
                          size="small"
                          aria-label="edit"
                          onClick={() => onEditClick(row)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Disable" arrow>
                        <IconButton
                          size="small"
                          aria-label="disable"
                          onClick={() => handleDisableClick(row)}
                        >
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </StyledTableCell>
                  )}
                </StyledTableRow>
              ))
            ) : (
              <StyledTableRow>
                <StyledTableCell colSpan={user?.role === 'super_admin' ? 9 : 8} align="center" sx={{ py: 4 }}>
                  No data available
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[ 10,20,30,60,100]}
          component="div"
          count={rows?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Student List Modal */}
      <StudentListModal
        open={isModalOpen}
        onClose={handleCloseModal}
        studentData={selectedRow}
        fetchCoeData={fetchCoeData}
        status={studentListType}
      />

      <AdminListModal
        open={isAdmin}
        onClose={handleCloseAdminModal}
        selectedRow={selectedRow}
        fetchCoeData={fetchCoeData}
        user={user}
        adminListType={adminListType}
      />

      {/* Confirmation Dialog for COE Disable */}
      <Dialog
        open={confirmOpen}
        onClose={handleCancelDisable}
        PaperProps={{
          sx: {
            p: 3,
            borderRadius: 3,
            boxShadow: 6,
            minWidth: 400,
            maxWidth: 450,
          }
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 'bold',
            fontSize: '1.25rem',
            color: '#d32f2f',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <WarningAmberRoundedIcon fontSize="medium" color="error" />
          Disable COE
        </DialogTitle>

        <DialogContent>
          <DialogContentText sx={{ fontSize: 15, color: "#555", mt: 1 }}>
            Are you sure you want to disable <strong>{selectedCoe?.name}</strong>?
            <br />
            This will prevent access to associated learners and admins.
          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "flex-end", mt: 2 }}>
          <Button
            onClick={handleCancelDisable}
            variant="outlined"
            color="inherit"
            sx={{ textTransform: "capitalize", borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDisable}
            variant="contained"
            color="error"
            sx={{ textTransform: "capitalize", borderRadius: 2 }}
          >
            Yes, Disable
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CoeTable;

